import { normalizeCampaignSlug } from './slug';
import type { ChallengeCardBackground, PayloadBuilderState, ProgramConfig } from './types';

export function allStringsNonEmpty(values: Array<string>): boolean {
  return values.length > 0 && values.every((value) => value.trim());
}

export function hasValidStatusMatchPrograms(programs: Array<ProgramConfig>): boolean {
  return programs.some(
    (program) =>
      program.programName.trim() &&
      program.logoImageId.trim() &&
      program.tierMappings.some((mapping) => mapping.sourceTier.trim() && mapping.targetSocieteTier.trim()),
  );
}

export function hasValidStatusMatchConditions(state: PayloadBuilderState): boolean {
  return (
    state.conditions.length > 0 &&
    state.conditions.every((condition) => condition.title.trim() && condition.description.trim())
  );
}

export function hasValidStatusMatchTextSteps(steps: Array<{ title: string; description: string }>): boolean {
  return steps.length > 0 && steps.every((step) => step.title.trim() && step.description.trim());
}

export function hasValidStatusMatchChallengeCard(state: PayloadBuilderState): boolean {
  if (
    !state.challengeCardTitleSuffix.trim() ||
    !state.challengeCardSubtitleBefore.trim() ||
    !state.challengeCardSubtitleAfter.trim()
  ) {
    return false;
  }

  return hasValidChallengeCardBackground(state.challengeCardBackground);
}

export function hasValidChallengeCardBackground(background: ChallengeCardBackground): boolean {
  if (background.type === 'color') {
    return Boolean(background.value?.trim());
  }
  if (background.type === 'image') {
    return Boolean(background.imageId?.trim());
  }
  return true;
}

export function hasValidStatusMatchEvidence(state: PayloadBuilderState): boolean {
  return (
    state.evidenceMinPhotos > 0 &&
    state.evidenceVerificationImageId.trim() &&
    allStringsNonEmpty(state.evidencePhotoHints) &&
    allStringsNonEmpty(state.evidenceAcceptedFormats) &&
    allStringsNonEmpty(state.evidenceProofRequirements)
  );
}

export function hasValidStatusMatchCampaignFields(state: PayloadBuilderState): boolean {
  return Boolean(
    state.termsSlug.trim() &&
      state.applicationPeriodFrom.trim() &&
      state.applicationPeriodTo.trim() &&
      (state.allowedRegions?.length ?? 0) > 0,
  );
}

export function hasValidStatusMatchHero(state: PayloadBuilderState): boolean {
  return Boolean(
    state.heroTitle.trim() &&
      state.heroSubtitle.trim() &&
      state.heroDescription.trim() &&
      state.heroCtaText.trim() &&
      state.heroImageId.trim(),
  );
}

export function isStatusMatchPayloadComplete(state: PayloadBuilderState): boolean {
  return Boolean(
    state.campaignId.trim() &&
      normalizeCampaignSlug(state.slug) &&
      hasValidStatusMatchHero(state) &&
      hasValidStatusMatchCampaignFields(state) &&
      hasValidStatusMatchPrograms(state.programs) &&
      hasValidStatusMatchConditions(state) &&
      hasValidStatusMatchTextSteps(state.howItWorks) &&
      hasValidStatusMatchChallengeCard(state) &&
      hasValidStatusMatchEvidence(state) &&
      hasValidStatusMatchTextSteps(state.successSteps),
  );
}
